import type { FC } from "react";
import Head from "next/head";
import styled from "styled-components";
import BreadCrumb from "components/BreadCrumb";
import { space, colors } from "utils/theme";
import { useLocale } from "hooks";
import {
  generateCanonical,
  generateHrefLang,
  limitContent,
} from "utils/newFunction";
import { useRouter } from "next/router";
import { responsive } from "utils/theme";
import { useHeader } from "./PageContext";
import { useEffect } from "react";
import { PRODUCTION_URL, ENVIRONMENT } from "utils/constants";
interface IProps {
  [key: string]: any;
  className?: string;
  title?: string;
  breadCrumb?: any[];
  metaTag?: any;
  useDefaultHrefLang: boolean;
  changeLanguageLink?: string;
  noIndex?: boolean;
}

const PageContainerCMP: FC<IProps> = ({
  className,
  breadCrumb,
  children,
  metaTag,
  useDefaultHrefLang = false,
  changeLanguageLink,
  noIndex,
}) => {
  const { isFA, locale } = useLocale();
  const router = useRouter();
  const hreflangObject = useDefaultHrefLang
    ? generateHrefLang(router?.asPath?.split?.("?")?.[0] || "")
    : null;
  const { setChangeLanguageLink } = useHeader();

  useEffect(() => {
    setChangeLanguageLink(
      changeLanguageLink || (useDefaultHrefLang ? router.pathname : "/") || "/"
    );
  }, [changeLanguageLink, router.locale, router.pathname]);

  const renderHrefLang = () => {
    let path = router.asPath?.split("?")?.[0];
    if (router.query?.tab && !path?.includes("/professors")) {
      path = path + `?tab=${router.query?.tab}`;
    }
    if (["/", "/?"].includes(path)) {
      path = "";
    }
    path = path === "/" ? "" : path;
    const MAPPED = {
      static: {
        "fa-IR": hreflangObject?.fa,
        "en-GB": hreflangObject?.en,
        "x-default": hreflangObject?.fa,
      },
      fa: {
        "fa-IR": `${PRODUCTION_URL}${path}`,
        "en-GB": `${PRODUCTION_URL}${changeLanguageLink}`,
        "x-default": `${PRODUCTION_URL}${path}`,
      },
      en: {
        "en-GB": `${PRODUCTION_URL}/${locale}${path}`,
        "fa-IR": `${PRODUCTION_URL}${changeLanguageLink}`,
        "x-default": `${PRODUCTION_URL}${changeLanguageLink}`,
      },
      notProvided: {},
    };

    return Object.entries(
      MAPPED[
        useDefaultHrefLang
          ? "static"
          : changeLanguageLink
          ? locale || "fa"
          : "notProvided"
      ]
    ).map(([hrefLang, href], i) => (
      <link
        key={i}
        rel="alternate"
        hrefLang={hrefLang}
        href={decodeURIComponent(href)}
      />
    ));
  };

  const renderFonts = () => {
    return (
      <>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        {!isFA && (
          <link
            href="https://fonts.googleapis.com/css2?family=Domine:wght@400;500;600;700&family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap"
            rel="stylesheet"
          />
        )}
      </>
    );
  };

  const renderTitle = () => {
    const titlePage = `${limitContent(metaTag?.title || "", 100)} | ${
      isFA ? "دانشگاه خاتم" : "Khatam University"
    }`;
    return (
      <>
        <title>{titlePage}</title>
        <meta property="og:title" content={titlePage} />
      </>
    );
  };

  const renderKeywords = () => {
    if (metaTag?.keywords) {
      return <meta name="keywords" content={metaTag?.keywords} />;
    }
  };

  const renderDescription = () => {
    if (metaTag?.description) {
      const limited = limitContent(metaTag?.description, 200);
      return ["description", "og:description"].map((name) => (
        <meta key={name} name={name} content={limited} />
      ));
    }
  };

  const renderOgUrl = () => {
    if (metaTag?.ogUrl) {
      return <meta property="og:url" content={metaTag?.ogUrl} />;
    }
  };

  const renderOgUImage = () => {
    if (metaTag?.ogImage) {
      return (
        <meta
          property="og:image"
          itemProp="image primaryImageOfPage"
          content={metaTag?.ogImage}
        />
      );
    }
  };

  const renderCanonical = () => {
    let path = router.asPath?.split?.("?")?.[0] || "";

    if (router.query.tab) {
      path = path + `?tab=${router.query.tab}`;
    }
    if (path === "/") {
      path = "";
    }
    const href = decodeURIComponent(
      metaTag?.canonical
        ? generateCanonical(metaTag?.canonical, "fa")
        : generateCanonical(path, locale)
    );
    return <link rel="canonical" href={href} />;
  };

  const renderNoIndex = () => {
    return (
      <meta
        name="robots"
        content={
          noIndex || ENVIRONMENT !== "production"
            ? "noindex,nofollow"
            : "index,follow"
        }
      />
    );
  };

  return (
    <>
      <Head>
        {renderNoIndex()}
        {renderTitle()}
        {renderFonts()}
        {renderKeywords()}
        {renderDescription()}
        {renderOgUrl()}
        {renderOgUImage()}
        {renderHrefLang()}
        {renderCanonical()}
        {renderNoIndex()}
      </Head>
      <div className={className}>
        <div className="page-content">
          {breadCrumb && <BreadCrumb items={breadCrumb} />}
          {children}
        </div>
      </div>
    </>
  );
};

const PageContainer = styled(PageContainerCMP)`
  .page-content {
    background-color: #f6f7fb;

    @media only screen and (min-height: 700px) {
      min-height: 300px;
    }

    @media only screen and (min-height: 800px) {
      min-height: 450px;
    }

    @media only screen and (min-height: 850px) {
      min-height: 500px;
    }

    @media only screen and (min-height: 900px) {
      min-height: 550px;
    }

    min-height: auto;
  }
  // min-height: 100vh;
  > .__page-inside {
    padding-top: calc(70px + 38px); //Height of the header and sub header

    @media only screen and (min-height: ${responsive.xxxl}px) {
      padding-top: calc(
        93.33px + 50.67px
      ); //Height of the header and sub header
    }

    min-height: calc(100vh - 360px);
    background: ${(props) => props?.bg ?? "white"};
    z-index: -2;
    box-sizing: border-box;
  }

  /* Forms English Common Styles */
  .select-search {
    * {
      text-align: left;
      direction: ltr;
    }
    .select-search__input:hover {
      border-color: ${colors.primary.dark};
    }
    .select-search__select {
      border-radius: 0;
      border-color: ${colors.primary.dark};
    }
    .select-search__value::after {
      left: initial;
      right: ${space(2)};
    }
  }
`;

export default PageContainer;
